'use client';

import React, { useEffect, useRef, useState } from 'react';
import { FileImage } from 'lucide-react';

type PdfJsModule = {
  getDocument: (options: {
    url?: string;
    data?: Uint8Array;
    disableWorker?: boolean;
    useWorkerFetch?: boolean;
    isEvalSupported?: boolean;
  }) => { promise: Promise<PdfDocument> };
  GlobalWorkerOptions: { workerSrc: string };
  version: string;
};

type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};

type PdfPage = {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
};

interface PdfThumbnailProps {
  url: string;
  alt?: string;
  className?: string;
  fallbackIcon?: React.ReactNode;
}

export default function PdfThumbnail({ url, alt, className, fallbackIcon }: PdfThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);

    const render = async () => {
      try {
        const pdfjsModule = await import('pdfjs-dist').catch(() =>
          import('pdfjs-dist/legacy/build/pdf.mjs')
        );
        const pdfjsLib = (pdfjsModule && 'default' in pdfjsModule
          ? pdfjsModule.default
          : pdfjsModule) as unknown as PdfJsModule;
        if (!pdfjsLib || typeof pdfjsLib.getDocument !== 'function') {
          throw new Error('Failed to load PDF viewer');
        }

        if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
          const base = typeof window !== 'undefined' ? window.location.origin : '';
          pdfjsLib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.mjs`;
        }

        const loadingTask = pdfjsLib.getDocument({
          url,
          disableWorker: true,
          useWorkerFetch: false,
          isEvalSupported: false,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Render at a fixed target width and scale the viewport accordingly
        const desiredWidth = 160;
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      } catch (err) {
        if (!cancelled) {
          console.error('[PdfThumbnail] Failed to render PDF thumbnail:', err);
          setError(true);
        }
      }
    };

    void render();

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        {fallbackIcon || <FileImage className="h-6 w-6 text-gray-400" />}
      </div>
    );
  }

  return <canvas ref={canvasRef} className={className} aria-label={alt || 'PDF thumbnail'} />;
}
